import {
  doc,
  increment,
  runTransaction,
  serverTimestamp,
  type Firestore,
  type Transaction,
} from 'firebase/firestore'
import tierConfiguration from '../config/tierConfiguration.json'
import { TIER_IDS, normalizeTierId, type TierId } from '../config/PricingConfig'
import { testingOverrideAllowsAllFeatures } from '../config/testingOverrides'
import { limitsLiftedForWorkspace } from '../utils/demoUnlock'
import { resolveTierLimit, getPlanLimitOverrides, type PlanLimitOverrides } from '../config/planLimitsCore'
import { firestoreDb } from './firebase'

export { TIER_IDS, normalizeTierId }
export type { TierId }
export type FeatureKey = keyof typeof tierConfiguration.tiers.FREE_TIER.limits

// True for any tier above FREE_TIER (also true when the testing override is
// enabled). Used by Explore's premium-template gate — see importTemplate.
export const isPremiumTier = (plan: unknown, workspace?: unknown): boolean => {
  if (limitsLiftedForWorkspace(workspace)) return true
  return normalizeTierId(plan) !== 'FREE_TIER'
}

export class PremiumTemplateRequiredError extends Error {
  readonly code = 'PREMIUM_TEMPLATE_REQUIRED'
  readonly templateId: string
  readonly plan: TierId
  constructor(templateId: string, plan: unknown) {
    super('PREMIUM_TEMPLATE_REQUIRED')
    this.name = 'PremiumTemplateRequiredError'
    this.templateId = templateId
    this.plan = normalizeTierId(plan)
  }
}

export class PaywallRequiredError extends Error {
  readonly code = 'PAYWALL_REQUIRED'
  readonly featureKey: FeatureKey
  readonly currentUsage: number
  readonly requestedAmount: number
  readonly limit: number
  readonly plan: TierId

  constructor(
    featureKey: FeatureKey,
    currentUsage: number,
    requestedAmount: number,
    limit: number,
    plan: TierId,
  ) {
    super('PAYWALL_REQUIRED')
    this.name = 'PaywallRequiredError'
    this.featureKey = featureKey
    this.currentUsage = currentUsage
    this.requestedAmount = requestedAmount
    this.limit = limit
    this.plan = plan
  }
}

// `workspace` (optional) lets a demo-unlocked workspace lift the limit (R35); omit it for tier display.
export const getTierLimit = (
  plan: unknown,
  featureKey: FeatureKey,
  workspace?: unknown,
  overrides: PlanLimitOverrides = getPlanLimitOverrides(),
): number => {
  return limitsLiftedForWorkspace(workspace)
    ? -1
    : resolveTierLimit(normalizeTierId(plan), featureKey, overrides)
}


export const assertWithinTierLimit = (
  plan: unknown,
  featureKey: FeatureKey,
  currentUsage: number,
  requestedAmount = 1,
  reservedUsage = 0,
  workspace?: unknown,
) => {
  if (!Number.isFinite(requestedAmount) || requestedAmount < 0) {
    throw new Error('requestedAmount must be a non-negative finite number.')
  }
  if (limitsLiftedForWorkspace(workspace)) return
  const normalizedPlan = normalizeTierId(plan)
  const limit = getTierLimit(normalizedPlan, featureKey)
  if (limit !== -1 && currentUsage + reservedUsage + requestedAmount > limit) {
    throw new PaywallRequiredError(featureKey, currentUsage + reservedUsage, requestedAmount, limit, normalizedPlan)
  }
}

const usageFromMetric = (data: Record<string, unknown> | undefined, field: 'currentUsage' | 'reservedUsage') => {
  const value = Number(field === 'currentUsage' ? data?.currentUsage ?? data?.used ?? data?.value ?? 0 : data?.reservedUsage ?? 0)
  return Number.isFinite(value) && value > 0 ? value : 0
}

export async function validateOperation(
  transaction: Transaction,
  workspaceId: string,
  featureKey: FeatureKey,
  requestedAmount = 1,
  db: Firestore = firestoreDb,
): Promise<void> {
  if (testingOverrideAllowsAllFeatures()) return
  const workspaceRef = doc(db, 'workspaces', workspaceId)
  const metricRef = doc(db, 'workspace_usage', workspaceId, 'metrics', featureKey)
  const [workspace, metric] = await Promise.all([
    transaction.get(workspaceRef),
    transaction.get(metricRef),
  ])
  if (!workspace.exists()) throw new Error('Workspace not found.')
  // Demo unlock (R35): decided after the workspace read, inside the transaction.
  if (limitsLiftedForWorkspace(workspace.data())) return
  assertWithinTierLimit(
    workspace.data().plan,
    featureKey,
    usageFromMetric(metric.data(), 'currentUsage'),
    requestedAmount,
    usageFromMetric(metric.data(), 'reservedUsage'),
  )
}

export async function runEnforcedTransaction<T>(
  workspaceId: string,
  featureKey: FeatureKey,
  requestedAmount: number,
  callback: (transaction: Transaction) => Promise<T>,
  db: Firestore = firestoreDb,
): Promise<T> {
  return runTransaction(db, async (transaction) => {
    await validateOperation(transaction, workspaceId, featureKey, requestedAmount, db)
    const result = await callback(transaction)
    transaction.set(doc(db, 'workspace_usage', workspaceId, 'metrics', featureKey), {
      currentUsage: increment(requestedAmount),
      updatedAt: serverTimestamp(),
    }, { merge: true })
    return result
  })
}
